/**
 * El modo oscuro: quién lo elige y dónde se recuerda.
 *
 * **Es una preferencia de la PERSONA, no del negocio.** El color de la marca sí
 * es del negocio —lo ve el cliente en los correos, es identidad— pero trabajar
 * con fondo claro u oscuro es comodidad de quien tiene la pantalla enfrente. Si
 * el dueño lo eligiera para todo el equipo, la recepcionista que no lo aguanta
 * se queda sin salida. Así que cada quien lo enciende en su propio navegador.
 *
 * Por eso se guarda en el navegador y no en la base: no hay nada que sincronizar
 * entre dispositivos que valga una columna nueva y una migración. Si el mismo
 * usuario entra desde otra computadora, vuelve a elegir una vez.
 *
 * Lo que NO hace: seguir al sistema operativo. El dueño pidió que se ELIJA.
 * Un `prefers-color-scheme` automático deja al usuario sin control y sorprende
 * al que tiene el equipo en oscuro y no quiere el sistema así.
 */
import { useCallback, useEffect, useState } from 'react';

const LLAVE = 'cavernicolapp_modo_oscuro';

function leer() {
  try { return localStorage.getItem(LLAVE) === 'si'; } catch { return false; }
}

export function useModoOscuro() {
  const [oscuro, setOscuro] = useState(leer);

  const alternar = useCallback(() => {
    setOscuro((v) => {
      const nuevo = !v;
      try { localStorage.setItem(LLAVE, nuevo ? 'si' : 'no'); } catch { /* modo privado */ }
      return nuevo;
    });
  }, []);

  // La clase va en `<html>` y no solo en el contenedor del panel: sin esto, los
  // diálogos y los menús desplegables se quedan claros. Radix los monta en un
  // «portal» al final del `<body>`, FUERA del árbol del panel, así que una clase
  // puesta solo en el armazón no los alcanza — y el resultado sería un modo
  // oscuro que se rompe justo al abrir cualquier ventana.
  //
  // La página pública y las pantallas legales no se ven afectadas porque este
  // hook solo lo usa el armazón del tenant: si nadie enciende el modo, la clase
  // nunca se pone.
  useEffect(() => {
    const raiz = document.documentElement;
    if (oscuro) {
      raiz.classList.add('modo-oscuro', 'dark');
    } else {
      raiz.classList.remove('modo-oscuro', 'dark');
    }
    return () => raiz.classList.remove('modo-oscuro', 'dark');
  }, [oscuro]);

  return [oscuro, alternar];
}
